import config from '../config';

/**
 * Connection Service
 * Handles connectivity checks with the backend server
 */

const TIMEOUT_MS = 10000; // 10 seconds timeout for health checks (increased to handle slow networks)

/**
 * Error types for better error handling
 */
export const ConnectionErrorType = {
  NETWORK_ERROR: 'NETWORK_ERROR', // No internet or DNS failure
  BACKEND_UNAVAILABLE: 'BACKEND_UNAVAILABLE', // Backend server not responding
  BACKEND_DEGRADED: 'BACKEND_DEGRADED', // Backend responding but services degraded
  TIMEOUT: 'TIMEOUT', // Request timed out
  UNKNOWN: 'UNKNOWN'
};

/**
 * Check backend health using /health endpoint
 * @param {boolean} retryOnce - Whether to retry once on transient failures
 * @returns {Promise<{status: string, error: null|object}>}
 */
export const checkBackendHealth = async (retryOnce = true) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

  try {
    const response = await fetch(`${config.apiUrl}/health`, {
      method: 'GET',
      signal: controller.signal,
      headers: {
        'Accept': 'application/json'
      }
    });

    clearTimeout(timeoutId);

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      // Backend responding but unhealthy
      // 503 = Service Unavailable (degraded service)
      if (response.status === 503) {
        return {
          status: 'degraded',
          errorType: ConnectionErrorType.BACKEND_DEGRADED,
          error: {
            message: 'Algunos servicios no están disponibles',
            details: data,
            statusCode: response.status
          }
        };
      }

      // Other HTTP errors
      return {
        status: 'unhealthy',
        errorType: ConnectionErrorType.BACKEND_UNAVAILABLE,
        error: {
          message: 'El servidor está experimentando problemas',
          details: data,
          statusCode: response.status
        }
      };
    }

    // Check if any services are degraded even with 200 OK
    if (data.status !== 'ok') {
      return {
        status: 'degraded',
        errorType: ConnectionErrorType.BACKEND_DEGRADED,
        error: {
          message: 'Algunos servicios no están disponibles',
          details: data.services,
          statusCode: response.status
        }
      };
    }

    return {
      status: 'healthy',
      error: null,
      data
    };

  } catch (error) {
    clearTimeout(timeoutId);

    // Determine error type
    let errorType = ConnectionErrorType.UNKNOWN;
    let message = 'Error al conectar con el servidor';

    if (error.name === 'AbortError') {
      errorType = ConnectionErrorType.TIMEOUT;
      message = 'El servidor tardó demasiado en responder';
    } else if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
      // Could be no internet or backend down
      errorType = ConnectionErrorType.NETWORK_ERROR;
      message = 'No se puede conectar con el servidor';
    } else if (error.code === 'ECONNREFUSED' || error.message.includes('ERR_CONNECTION_REFUSED')) {
      errorType = ConnectionErrorType.BACKEND_UNAVAILABLE;
      message = 'El servidor no está disponible';
    }

    // Retry once on transient errors (timeout or network glitches)
    if (retryOnce && (errorType === ConnectionErrorType.TIMEOUT || errorType === ConnectionErrorType.NETWORK_ERROR)) {
      console.log('[connectionService] Transient error detected, retrying once after 1s delay...');
      await new Promise(resolve => setTimeout(resolve, 1000));
      return checkBackendHealth(false); // Second attempt without retry
    }

    return {
      status: 'error',
      errorType,
      error: {
        message,
        originalError: error.message,
        code: error.code
      }
    };
  }
};

/**
 * Quick ping check (uses /ping endpoint for faster response)
 * @returns {Promise<boolean>}
 */
export const quickPing = async () => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 3000); // Shorter timeout for ping

  try {
    const response = await fetch(`${config.apiUrl}/ping`, {
      method: 'GET',
      signal: controller.signal
    });

    clearTimeout(timeoutId);
    return response.ok;

  } catch (error) {
    clearTimeout(timeoutId);
    return false;
  }
};

/**
 * Check if browser is online (navigator.onLine)
 * Note: This only checks if there's a network connection, not internet access
 * @returns {boolean}
 */
export const isOnline = () => {
  return typeof navigator !== 'undefined' && navigator.onLine;
};

/**
 * Check if we have actual internet connectivity by testing external service
 * @returns {Promise<boolean>}
 */
const checkExternalConnectivity = async () => {
  try {
    // Try to reach a reliable external service (Supabase in this case)
    // Using a small timeout to avoid long waits
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000);

    // We'll try to fetch from Supabase which should always be available
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
    if (supabaseUrl) {
      try {
        await fetch(supabaseUrl, {
          method: 'HEAD',
          signal: controller.signal,
          mode: 'no-cors' // Avoid CORS issues, we just want to know if it's reachable
        });
        clearTimeout(timeoutId);
        return true;
      } catch (error) {
        clearTimeout(timeoutId);
        // Even with no-cors, if fetch completes, there's internet
        // Only network errors mean no connectivity
        return error.name !== 'AbortError';
      }
    }

    clearTimeout(timeoutId);
    return true; // If no Supabase URL configured, assume internet is working
  } catch (error) {
    return false;
  }
};

/**
 * Comprehensive connection check
 * Checks both browser online status and backend health
 * Smart detection to distinguish between no internet vs backend unavailable
 * @returns {Promise<{isConnected: boolean, details: object}>}
 */
export const checkConnection = async () => {
  const browserOnline = isOnline();

  // First check: browser says we're offline
  if (!browserOnline) {
    return {
      isConnected: false,
      errorType: ConnectionErrorType.NETWORK_ERROR,
      details: {
        message: 'Sin conexión a Internet',
        browserOnline: false
      }
    };
  }

  // Second check: try to reach our backend
  const healthCheck = await checkBackendHealth();

  if (healthCheck.status === 'healthy') {
    return {
      isConnected: true,
      errorType: null,
      details: {
        message: 'Conectado',
        browserOnline: true,
        backendStatus: healthCheck.status
      }
    };
  }

  // If backend responded with degraded status (503), respect that classification
  if (healthCheck.status === 'degraded') {
    return {
      isConnected: false,
      errorType: ConnectionErrorType.BACKEND_DEGRADED,
      details: {
        message: healthCheck.error?.message || 'Algunos servicios no están disponibles',
        browserOnline: true,
        hasExternalConnectivity: true,
        backendStatus: healthCheck.status,
        ...healthCheck.error
      }
    };
  }

  // Backend did not respond at all - let's check if it's an internet issue or backend issue
  const hasInternet = await checkExternalConnectivity();

  if (!hasInternet) {
    // No internet at all
    return {
      isConnected: false,
      errorType: ConnectionErrorType.NETWORK_ERROR,
      details: {
        message: 'Sin conexión a Internet',
        browserOnline: true,
        hasExternalConnectivity: false
      }
    };
  }

  // We have internet but backend is not responding at all
  // This is clearly a backend issue, not an internet issue
  return {
    isConnected: false,
    errorType: ConnectionErrorType.BACKEND_UNAVAILABLE,
    details: {
      message: healthCheck.error?.message || 'El servidor no está disponible',
      browserOnline: true,
      hasExternalConnectivity: true,
      backendStatus: healthCheck.status,
      ...healthCheck.error
    }
  };
};

export default {
  checkBackendHealth,
  quickPing,
  isOnline,
  checkConnection,
  ConnectionErrorType
};
